import { useEffect, useState } from "react";
import styled from "styled-components";
import { useSnackbar } from "notistack";
import { styled as muiStyled } from "@mui/material/styles";

import { Grid, Stack, Typography } from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";

import CloseIcon from "@mui/icons-material/Close";

import { getAllUserList } from "src/api/user";
import { getAllAdminRole, updateAdminDetail } from "src/api/admin";

import font from "src/styles/font";
import device from "src/styles/device";
import { checkNotEmpty } from "src/common";
import { Black, Primary } from "src/styles/color";
import { useDialog } from "src/context/DialogContext";

import Dialog from "src/components/core/Dialog";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";

const CloseButton = styled(IconButton)`
  &.MuiIconButton-root {
    position: absolute;
    top: 16px;
    right: 16px;
    color: ${Black};
  }
`;

const WrapperBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const WrapperTextField = styled(Stack)`
  width: 100%;
`;

const SpanTitle = styled.div`
  font-size: ${font.size.md};
  font-weight: 600;
  line-height: 24px;

  @media ${device.mobileL} {
    font-size: ${font.size.sm};
  }
`;

const TextFieldLabel = styled.p`
  margin: 0;
  font-size: ${font.size.sm};
  font-weight: 400;
  line-height: 24px;

  @media ${device.mobileL} {
    font-size: ${font.size.xs};
    line-height: 16px;
  }
`;

const StyledTextField = styled(TextField)`
  .MuiInputBase-adornedEnd {
    padding-right: 2px;
  }
`;

const StyledDialog = muiStyled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": { borderRadius: 16 },
  "& .MuiDialogContent-root": {
    maxHeight: "500px",
    border: 0,
    padding: 24,
    paddingTop: 0,
  },
  "& .MuiDialog-container": {
    "& .MuiPaper-root": { width: "100%", maxWidth: 640 },
  },
  "& .MuiDialogActions-root": { justifyContent: "center" },
  "& .MuiDialogActions-root>:not(:first-of-type)": { marginLeft: 24 },

  [theme.breakpoints.down("sm")]: {
    "& .MuiPaper-root": { margin: 24 },
    "& .MuiDialogContent-root": { padding: 16, paddingTop: 0 },
    "& .MuiDialogActions-root": { padding: 16 },
  },
}));

const AddAdminDialog = ({ setIsLoading, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { openDialog } = useDialog();

  const defaultPermission = {
    role: "",
    permission: {
      admins: {
        can_view: false,
        can_create: false,
        can_edit: false,
        can_delete: false,
      },
      users: {
        can_view: false,
        can_edit: false,
        // can_delete: false,
      },
      sellers: {
        can_view: false,
        can_edit: false,
        can_approve: false,
        can_setting: false,
      },
      dashboards: {
        can_view: false,
      },
      products: {
        can_view: false,
        can_create: false,
        can_edit: false,
        can_approve: false,
        can_hold_delete: false,
        can_public: false,
        can_update_stock: false,
        can_view_costPrice: false,
        // can_view_specialPrice: false,
      },
      reviews: {
        can_view: false,
        can_create: false,
        can_edit: false,
        can_reply: false,
        can_approve: false,
        can_public: false,
      },
      quotations: {
        can_view: false,
        can_create: false,
        can_replace: false,
        can_approve: false,
      },
      orders: {
        can_view_sampleRequest: false,
        can_approve_sampleRequest: false,
        can_view_purchaseOrder: false,
        can_update_purchaseOrder: false,
        can_view_shippingOrder: false,
        can_update_shippingOrder: false,
        can_view_cancelOrder: false,
        can_update_cancelOrder: false,
      },
      promo_codes: {
        can_view: false,
        can_create: false,
        can_edit: false,
        can_delete: false,
      },
      sale_features: {
        can_view_adminQuotation: false,
        can_view_saleQuotation: false,
        can_create_saleQuotation: false,
        can_view_saleOrder: false,
        can_create_saleOrder: false,
      },
      features: {
        can_manage_landingPage: false,
        can_manage_xclusiveDeal: false,
        can_manage_shopThisMood: false,
        can_manage_blog: false,
      },
      wallplast: {
        can_view: false,
      },
    },
  };
  const defaultData = {
    _id: null,
    email: "",
    first_name: "",
    last_name: "",
    role_id: 1,
    admin_permission: defaultPermission,
  };
  const [adminData, setAdminData] = useState(defaultData);
  const [userList, setUserList] = useState([]);
  const [adminRoleList, setAdminRoleList] = useState([]);

  const [isDataLoading, setIsDataLoading] = useState(false);

  const fetchAllRole = async () => {
    try {
      setIsDataLoading(true);
      const data = await getAllAdminRole();
      setAdminRoleList(data);
    } catch (err) {
      console.error("role err:", err);
    } finally {
      setIsDataLoading(false);
    }
  };

  const fetchUser = async () => {
    try {
      setIsDataLoading(true);
      const data = await getAllUserList();
      setUserList(
        data.rows.map((user) => ({
          ...user,
          name:
            user.register_type === 1 // email-pwd
              ? checkNotEmpty(user?.first_name) &&
                checkNotEmpty(user?.last_name)
                ? `${user?.first_name} ${user?.last_name}`
                : "-"
              : user.register_type === 2 // facebook
                ? user.facebook_name
                : user.register_type === 3 // google
                  ? user.google_name
                  : "-",
        })),
      );
    } catch (err) {
      console.error("user err:", err);
    } finally {
      setIsDataLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
    fetchAllRole();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectUser = async (value) => {
    setAdminData({
      ...value,
      role_id: 1,
      admin_permission: defaultPermission,
    });
  };

  const handleSelectRole = async (value) => {
    setAdminData((prev) => ({
      ...prev,
      admin_permission: {
        role: value,
        permission: value.default_permission,
      },
    }));
  };

  const isFormValid = [
    "email",
    "first_name",
    "last_name",
    "admin_permission",
  ].every((required) => {
    let objVal = adminData[required];

    if (required === "admin_permission")
      return Object.keys(objVal).every((req) => {
        let subObj = objVal[req];
        return checkNotEmpty(subObj);
      });
    return checkNotEmpty(objVal);
  });

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      const updateParams = {
        ...adminData,
        role_id: 1,
        admin_permission: JSON.stringify({
          ...adminData?.admin_permission,
          role: adminData?.admin_permission?.role?._id,
        }),
      };

      await updateAdminDetail(adminData?._id, updateParams);
      enqueueSnackbar(`Add Admin Success!`, { variant: "success" });
      await onSuccess();
    } catch (err) {
      console.error("err:", err.response);
      enqueueSnackbar(err?.response?.data?.message, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    limit: 100,
  });

  return (
    <StyledDialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={true}
    >
      <DialogTitle
        sx={{
          p: { sm: 3, xs: 2 },
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontSize: { sm: font.size.lg, xs: font.size.md },
          fontWeight: 600,
          span: { color: Primary, fontWeight: 400 },
        }}
      >
        เพิ่ม Admin
        <CloseButton aria-label="close" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </CloseButton>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <WrapperBox>
              <SpanTitle>ค้นหาผู้ใช้งาน</SpanTitle>

              <WrapperTextField>
                <Autocomplete
                  aria-required={true}
                  id="search-user-select"
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  disableClearable
                  loading={isDataLoading}
                  options={userList}
                  value={adminData}
                  renderOption={(props, option) => (
                    <li {...props} key={`user-select-${option?._id}`}>
                      <Stack>
                        <Typography variant="subtitle1">
                          {option?.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: font.size.sm }}
                        >
                          {option?.email}
                        </Typography>
                      </Stack>
                    </li>
                  )}
                  onChange={(_, newValue) => {
                    handleSelectUser(newValue);
                  }}
                  getOptionLabel={(option) =>
                    option?.name
                      ? `${option?.name} - (` + option?.email + `)`
                      : ""
                  }
                  filterOptions={filterOptions}
                  fullWidth
                  sx={{
                    "&.MuiAutocomplete-root .MuiOutlinedInput-root": {
                      padding: "12px 16px",
                    },
                    ".MuiInputBase-input.MuiOutlinedInput-input": {
                      padding: 0,
                    },
                  }}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      placeholder="ค้นหาจากชื่อ/อีเมล"
                      // error={!checkNotEmpty(adminData?.email)}
                    />
                  )}
                />
              </WrapperTextField>
            </WrapperBox>
          </Grid>

          {!!adminData?.email ? (
            <>
              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12}>
                <SpanTitle>ข้อมูลส่วนตัว</SpanTitle>
              </Grid>

              <Grid item xs={12} md={6}>
                <WrapperTextField gap={0.5}>
                  <TextFieldLabel>ชื่อ</TextFieldLabel>
                  <StyledTextField
                    variant="outlined"
                    fullWidth
                    value={adminData?.first_name || ""}
                    onChange={(e) =>
                      setAdminData((prev) => ({
                        ...prev,
                        first_name: e.target.value,
                      }))
                    }
                    error={!checkNotEmpty(adminData?.first_name)}
                  />
                </WrapperTextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <WrapperTextField gap={0.5}>
                  <TextFieldLabel>นามสกุล</TextFieldLabel>
                  <StyledTextField
                    variant="outlined"
                    fullWidth
                    value={adminData?.last_name || ""}
                    onChange={(e) =>
                      setAdminData((prev) => ({
                        ...prev,
                        last_name: e.target.value,
                      }))
                    }
                    error={!checkNotEmpty(adminData?.last_name)}
                  />
                </WrapperTextField>
              </Grid>

              <Grid item xs={12}>
                <WrapperTextField gap={0.5}>
                  <TextFieldLabel>email</TextFieldLabel>
                  <StyledTextField
                    variant="outlined"
                    fullWidth
                    disabled
                    value={adminData?.email}
                    error={!checkNotEmpty(adminData?.email)}
                  />
                </WrapperTextField>
              </Grid>

              <Grid item xs={12}>
                <WrapperTextField gap={0.5}>
                  <TextFieldLabel>Role</TextFieldLabel>
                  <Autocomplete
                    aria-required={true}
                    id="search-user-select"
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    disableClearable
                    loading={isDataLoading}
                    options={adminRoleList}
                    value={adminData?.admin_permission?.role || ""}
                    onChange={(event, newValue) => {
                      handleSelectRole(newValue);
                    }}
                    getOptionLabel={(option) => {
                      return option?.role_name || "";
                    }}
                    filterOptions={filterOptions}
                    fullWidth
                    sx={{
                      "&.MuiAutocomplete-root .MuiOutlinedInput-root": {
                        padding: "12px 16px",
                      },
                      ".MuiInputBase-input.MuiOutlinedInput-input": {
                        padding: 0,
                      },
                    }}
                    renderInput={(params) => (
                      <StyledTextField
                        {...params}
                        placeholder="เลือก Role"
                        error={
                          !checkNotEmpty(adminData?.admin_permission?.role)
                        }
                      />
                    )}
                  />
                </WrapperTextField>
              </Grid>
            </>
          ) : null}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="outlined"
          color="secondary"
          size="large"
          onClick={() => onClose()}
          fullWidth
          sx={{ maxWidth: 180 }}
        >
          ยกเลิก
        </Button>

        <Button
          variant="contained"
          size="large"
          disabled={!isFormValid}
          onClick={() =>
            openDialog({
              type: "confirm",
              title: `คุณต้องการเพิ่ม Admin ใช่หรือไม่`,
              onConfirm: () => {
                onSubmit();
              },
              maxWidth: 342,
            })
          }
          fullWidth
          sx={{ maxWidth: 180 }}
        >
          ยืนยัน
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default AddAdminDialog;
